import React, { useState, useEffect } from 'react';
import { 
  TrendingUp, Building2, Calendar, DollarSign, CheckCircle2, AlertCircle, 
  RefreshCw, Percent, Hash, CreditCard, Clock, FileText, Plus, X, ShieldAlert,
  ArrowDownRight, ArrowUpRight, Check, Printer, Download
} from 'lucide-react';
import Cookies from 'js-cookie';
import { apiFetch } from '../services/api';

const API_BASE_URL = process.env.REACT_APP_API_BASE_URL || 'http://localhost:8001';

const fmt = (n) => Number(n || 0).toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 });

const today = () => new Date().toISOString().slice(0, 10);
const firstOfMonth = () => {
  const d = new Date();
  return new Date(d.getFullYear(), d.getMonth(), 1).toISOString().slice(0, 10);
};

const RevenueReport = () => {
  const [data, setData] = useState(null);
  const [clinics, setClinics] = useState([]);
  const [selectedClinicId, setSelectedClinicId] = useState('all');
  const [loading, setLoading] = useState(true);
  const [fromDate, setFromDate] = useState(firstOfMonth());
  const [toDate, setToDate] = useState(today());
  const [activeTab, setActiveTab] = useState('commission'); // 'commission' | 'subscription' | 'settlements'
  const [settlements, setSettlements] = useState([]);
  const [settlementsLoading, setSettlementsLoading] = useState(false);
  const [appointments, setAppointments] = useState([]);
  const [appointmentsLoading, setAppointmentsLoading] = useState(false);
  const [alert, setAlert] = useState({ show: false, type: '', message: '' });

  // Settlement Modal State
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [modalForm, setModalForm] = useState({
    clinic_id: '',
    settlement_type: 'commission',
    amount: '',
    period_from: firstOfMonth(),
    period_to: today(),
    payment_reference: '',
    notes: '',
  });
  const [savingSettlement, setSavingSettlement] = useState(false);

  const showAlert = (type, message) => {
    setAlert({ show: true, type, message });
    setTimeout(() => setAlert({ show: false, type: '', message: '' }), 4000);
  };

  const fetchClinicsList = async () => {
    try {
      const adminId = Cookies.get('adminid') || localStorage.getItem('adminid') || '0';
      const res = await apiFetch(`${API_BASE_URL}/admin/admingetclinicdetails/${adminId}`);
      const json = await res.json();
      if (json.status === 1 && Array.isArray(json.data)) {
        setClinics(json.data);
      }
    } catch (_) {}
  };

  const fetchReport = async () => {
    setLoading(true);
    try {
      const clinicParam = selectedClinicId && selectedClinicId !== 'all' ? `&clinic_id=${selectedClinicId}` : '';
      const res = await apiFetch(`${API_BASE_URL}/admin/revenue-report?from_date=${fromDate}&to_date=${toDate}${clinicParam}`);
      const json = await res.json();
      if (json.status === 1) {
        setData(json.data);
      } else {
        showAlert('error', json.msg || 'Failed to load report');
      }
    } catch (err) {
      showAlert('error', err.message);
    } finally {
      setLoading(false);
    }
  };

  const fetchSettlements = async () => {
    setSettlementsLoading(true);
    try {
      const clinicParam = selectedClinicId && selectedClinicId !== 'all' ? `?clinic_id=${selectedClinicId}` : '';
      const res = await apiFetch(`${API_BASE_URL}/admin/clinic-settlements${clinicParam}`);
      const json = await res.json();
      if (json.status === 1 && Array.isArray(json.data)) {
        setSettlements(json.data);
      }
    } catch (err) {
      console.warn('Failed to load settlements:', err.message);
    } finally {
      setSettlementsLoading(false);
    }
  };

  const fetchAppointments = async () => {
    setAppointmentsLoading(true);
    try {
      const clinicParam = selectedClinicId && selectedClinicId !== 'all' ? `&clinic_id=${selectedClinicId}` : '';
      const res = await apiFetch(`${API_BASE_URL}/admin/revenue-appointments?from_date=${fromDate}&to_date=${toDate}${clinicParam}`);
      const json = await res.json();
      if (json.status === 1) setAppointments(json.data || []);
    } catch (_) {}
    finally { setAppointmentsLoading(false); }
  };

  const printAppointments = () => {
    const win = window.open('', '_blank');
    const rows = appointments.map((a, i) => `
      <tr>
        <td>${i + 1}</td>
        <td>${a.book_date || '-'}</td>
        <td>${a.book_time || '-'}</td>
        <td>${a.patient_name || '-'}</td>
        <td>${a.clinic_name || '-'}</td>
        <td>${a.doctor_name || '-'}</td>
        <td>${a.service_type || '-'}</td>
        <td style="text-align:right">${Number(a.book_amount||0).toFixed(2)}</td>
        <td style="text-align:right">${Number(a.commission_amount||0).toFixed(2)}</td>
      </tr>`).join('');
    const total = appointments.reduce((s,a) => s + Number(a.book_amount||0), 0);
    const totalComm = appointments.reduce((s,a) => s + Number(a.commission_amount||0), 0);
    win.document.write(`<!DOCTYPE html><html><head><title>Appointment Revenue Report</title>
      <style>
        body{font-family:Arial,sans-serif;font-size:12px;margin:20px}
        h2{text-align:center;margin-bottom:4px}
        p.sub{text-align:center;color:#555;margin-bottom:16px}
        table{width:100%;border-collapse:collapse}
        th,td{border:1px solid #ccc;padding:6px 8px}
        th{background:#0d9488;color:#fff;font-weight:600}
        tr:nth-child(even){background:#f9fafb}
        tfoot td{font-weight:bold;background:#f1f5f9}
        @media print{button{display:none}}
      </style></head><body>
      <h2>Appointment Revenue Report</h2>
      <p class="sub">Period: ${fromDate} to ${toDate}</p>
      <table>
        <thead><tr>
          <th>#</th><th>Date</th><th>Time</th><th>Patient</th><th>Clinic</th>
          <th>Doctor</th><th>Service</th><th>Amount</th><th>Commission</th>
        </tr></thead>
        <tbody>${rows}</tbody>
        <tfoot><tr>
          <td colspan="7">Total (${appointments.length} appointments)</td>
          <td style="text-align:right">${total.toFixed(2)}</td>
          <td style="text-align:right">${totalComm.toFixed(2)}</td>
        </tr></tfoot>
      </table>
      <br/><button onclick="window.print()">Print</button>
      </body></html>`);
    win.document.close();
    win.focus();
    setTimeout(() => win.print(), 500);
  };

  const downloadAppointmentsCSV = () => {
    const headers = ['#','Date','Time','Patient','Clinic','Doctor','Service','Amount','Commission'];
    const rows = appointments.map((a, i) => [
      i+1, a.book_date||'', a.book_time||'', a.patient_name||'', a.clinic_name||'',
      a.doctor_name||'', a.service_type||'', Number(a.book_amount||0).toFixed(2),
      Number(a.commission_amount||0).toFixed(2)
    ]);
    const csv = [headers, ...rows].map(r => r.map(c => `"${c}"`).join(',')).join('\n');
    const blob = new Blob([csv], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url; a.download = `appointments_${fromDate}_${toDate}.csv`;
    a.click(); URL.revokeObjectURL(url);
  };

  useEffect(() => {
    fetchClinicsList();
  }, []);

  useEffect(() => {
    fetchReport();
    if (activeTab === 'settlements') {
      fetchSettlements();
    }
  }, [fromDate, toDate, selectedClinicId]);

  useEffect(() => {
    if (activeTab === 'settlements') {
      fetchSettlements();
    }
    if (activeTab === 'appointments') {
      fetchAppointments();
    }
  }, [activeTab]);

  const setRange = (range) => {
    const d = new Date();
    let from;
    if (range === 'today') {
      from = today();
      setFromDate(from); setToDate(today());
    } else if (range === 'week') {
      const day = d.getDay();
      from = new Date(d); from.setDate(d.getDate() - day);
      setFromDate(from.toISOString().slice(0, 10)); setToDate(today());
    } else if (range === 'month') {
      setFromDate(firstOfMonth()); setToDate(today());
    } else if (range === 'last_month') {
      const first = new Date(d.getFullYear(), d.getMonth() - 1, 1);
      const last  = new Date(d.getFullYear(), d.getMonth(), 0);
      setFromDate(first.toISOString().slice(0, 10)); setToDate(last.toISOString().slice(0, 10));
    } else if (range === 'year') {
      setFromDate(`${d.getFullYear()}-01-01`); setToDate(today());
    }
  };

  const openSettlementModal = (clinic, type = 'commission', suggestedAmount = 0) => {
    setModalForm({
      clinic_id: clinic ? clinic.clinic_id : (selectedClinicId !== 'all' ? selectedClinicId : ''),
      settlement_type: type,
      amount: suggestedAmount > 0 ? suggestedAmount : '',
      period_from: fromDate,
      period_to: toDate,
      payment_reference: '',
      notes: '',
    });
    setIsModalOpen(true);
  };

  const handleRecordSettlement = async (e) => {
    e.preventDefault();
    if (!modalForm.clinic_id) {
      alert('Please select a clinic');
      return;
    }
    if (!modalForm.amount || Number(modalForm.amount) <= 0) {
      alert('Please enter a valid settlement amount');
      return;
    }

    setSavingSettlement(true);
    try {
      const res = await apiFetch(`${API_BASE_URL}/admin/clinic-settlement`, {
        method: 'POST',
        body: JSON.stringify(modalForm),
      });
      const json = await res.json();
      if (json.status === 1) {
        showAlert('success', 'Settlement recorded successfully');
        setIsModalOpen(false);
        fetchReport();
        fetchSettlements();
      } else {
        showAlert('error', json.msg || 'Failed to record settlement');
      }
    } catch (err) {
      showAlert('error', err.message);
    } finally {
      setSavingSettlement(false);
    }
  };

  const s = data?.summary || {};
  const commClinics = data?.byClinic || [];
  const subClinics = data?.subscriptionClinics || [];

  return (
    <div className="content-section">
      <div className="content-header">
        <div>
          <h1>Revenue &amp; Settlement Management</h1>
          <p className="text-xs text-slate-400 font-medium mt-1">Platform commissions, clinic subscriptions, and payout settlements</p>
        </div>
        <div className="header-actions flex items-center gap-2">
          <button 
            className="create-btn flex items-center gap-2 bg-slate-800 hover:bg-slate-900"
            onClick={() => openSettlementModal(null, 'commission', 0)}
          >
            <Plus size={16} /> Record Settlement
          </button>
          <button className="create-btn flex items-center gap-2" onClick={fetchReport} disabled={loading}>
            <RefreshCw size={16} className={loading ? 'animate-spin' : ''} /> Refresh
          </button>
        </div>
      </div>

      {alert.show && (
        <div className={`mb-6 p-4 rounded-xl text-sm font-semibold flex items-center gap-3 ${alert.type === 'success' ? 'bg-emerald-50 border border-emerald-200 text-emerald-800' : 'bg-red-50 border border-red-200 text-red-800'}`}>
          {alert.type === 'success' ? <CheckCircle2 size={18} /> : <AlertCircle size={18} />}
          <span>{alert.message}</span>
        </div>
      )}

      {/* Filter Bar: Clinic Selector & Date Range */}
      <div className="bg-white rounded-2xl border border-slate-100 shadow-sm p-4 mb-6">
        <div className="flex flex-wrap items-center gap-4">
          {/* Clinic Selector */}
          <div className="flex items-center gap-2 min-w-[220px]">
            <Building2 size={16} className="text-slate-400" />
            <select
              value={selectedClinicId}
              onChange={(e) => setSelectedClinicId(e.target.value)}
              className="px-3 py-2 rounded-xl border border-slate-200 text-sm font-medium focus:outline-none focus:border-teal-600 w-full"
            >
              <option value="all">All Clinics (Consolidated)</option>
              {clinics.map((c) => (
                <option key={c.id} value={c.id}>
                  {c.vendor_contact || c.vendor_name || `Clinic #${c.id}`} (ID: {c.id})
                </option>
              ))}
            </select>
          </div>

          {/* Date Range */}
          <div className="flex items-center gap-2">
            <Calendar size={16} className="text-slate-400" />
            <input 
              type="date" 
              value={fromDate} 
              onChange={e => setFromDate(e.target.value)}
              className="px-3 py-2 rounded-xl border border-slate-200 text-sm focus:outline-none focus:border-teal-500 font-medium" 
            />
            <span className="text-xs text-slate-400 font-medium">to</span>
            <input 
              type="date" 
              value={toDate} 
              onChange={e => setToDate(e.target.value)}
              className="px-3 py-2 rounded-xl border border-slate-200 text-sm focus:outline-none focus:border-teal-500 font-medium" 
            />
            <button onClick={fetchReport} className="px-4 py-2 rounded-xl bg-teal-600 text-white text-xs font-bold hover:bg-teal-700 transition-all">
              Apply
            </button>
          </div>

          {/* Quick Date Presets */}
          <div className="flex items-center gap-1.5 ml-auto flex-wrap">
            {[['today','Today'],['week','This Week'],['month','This Month'],['last_month','Last Month'],['year','This Year']].map(([k,l]) => (
              <button 
                key={k} 
                onClick={() => setRange(k)}
                className="px-3 py-1.5 rounded-lg border border-slate-200 text-xs font-semibold text-slate-600 hover:border-teal-400 hover:text-teal-700 hover:bg-teal-50 transition-all"
              >
                {l}
              </button>
            ))}
          </div>
        </div>
      </div>

      {loading ? (
        <div className="loading"><div className="spinner"></div></div>
      ) : !data ? null : (
        <>
          {/* Financial Summary Cards */}
          <div className="grid grid-cols-2 lg:grid-cols-5 gap-4 mb-6">
            {[
              { label: 'Total Bookings', value: Number(s.total_bookings || 0).toLocaleString(), icon: Hash, color: '#2563eb', bg: '#eff6ff', border: '#bfdbfe' },
              { label: 'Patient Revenue', value: `₹ ${fmt(s.total_revenue)}`, icon: DollarSign, color: '#0d9488', bg: '#f0fdfa', border: '#99f6e4' },
              { label: 'Commission Earned', value: `₹ ${fmt(s.total_commission)}`, icon: Percent, color: '#9333ea', bg: '#fdf4ff', border: '#e9d5ff' },
              { label: 'Commission Settled', value: `₹ ${fmt(s.total_commission_settled)}`, icon: CheckCircle2, color: '#16a34a', bg: '#f0fdf4', border: '#bbf7d0' },
              { label: 'Pending to Collect', value: `₹ ${fmt(s.pending_commission)}`, icon: Clock, color: '#ea580c', bg: '#fff7ed', border: '#fed7aa' },
            ].map(({ label, value, icon: Icon, color, bg, border }) => (
              <div key={label} className="rounded-2xl p-4 border shadow-sm" style={{ background: bg, borderColor: border }}>
                <div className="flex items-center gap-2 mb-2">
                  <div className="w-8 h-8 rounded-lg flex items-center justify-center" style={{ background: color + '18' }}>
                    <Icon size={16} style={{ color }} />
                  </div>
                  <span className="text-[11px] font-bold uppercase tracking-wider" style={{ color: color + 'cc' }}>{label}</span>
                </div>
                <p className="text-xl font-bold" style={{ color }}>{value}</p>
              </div>
            ))}
          </div>

          {/* Navigation Tabs */}
          <div className="flex items-center gap-2 border-b border-slate-200 mb-6">
            <button
              onClick={() => setActiveTab('commission')}
              className={`pb-3 px-4 text-xs font-bold uppercase tracking-wider flex items-center gap-2 border-b-2 transition-all ${
                activeTab === 'commission'
                  ? 'border-teal-600 text-teal-700'
                  : 'border-transparent text-slate-400 hover:text-slate-700'
              }`}
            >
              <Percent size={14} />
              Commission Model &amp; Settlements ({commClinics.length})
            </button>
            <button
              onClick={() => setActiveTab('subscription')}
              className={`pb-3 px-4 text-xs font-bold uppercase tracking-wider flex items-center gap-2 border-b-2 transition-all ${
                activeTab === 'subscription'
                  ? 'border-teal-600 text-teal-700'
                  : 'border-transparent text-slate-400 hover:text-slate-700'
              }`}
            >
              <Calendar size={14} />
              Subscription Plans &amp; Validity ({subClinics.length})
            </button>
            <button
              onClick={() => setActiveTab('settlements')}
              className={`pb-3 px-4 text-xs font-bold uppercase tracking-wider flex items-center gap-2 border-b-2 transition-all ${
                activeTab === 'settlements'
                  ? 'border-teal-600 text-teal-700'
                  : 'border-transparent text-slate-400 hover:text-slate-700'
              }`}
            >
              <FileText size={14} />
              Settlement History
            </button>
            <button
              onClick={() => setActiveTab('appointments')}
              className={`pb-3 px-4 text-xs font-bold uppercase tracking-wider flex items-center gap-2 border-b-2 transition-all ${
                activeTab === 'appointments'
                  ? 'border-teal-600 text-teal-700'
                  : 'border-transparent text-slate-400 hover:text-slate-700'
              }`}
            >
              <Calendar size={14} />
              Appointments Detail
            </button>
          </div>

          {/* TAB 1: Commission Model Clinics */}
          {activeTab === 'commission' && (
            <div className="bg-white rounded-2xl border border-slate-100 shadow-sm p-5">
              <div className="flex items-center justify-between mb-4">
                <h2 className="text-sm font-bold text-slate-700 flex items-center gap-2">
                  <Percent size={16} className="text-teal-600" />
                  Commission Breakdown by Clinic
                </h2>
                <span className="text-xs text-slate-400 font-medium">Calculated from patient appointment bookings</span>
              </div>

              {commClinics.length === 0 ? (
                <div className="text-center py-10 text-slate-400 text-sm">No bookings recorded for this period.</div>
              ) : (
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="text-xs text-slate-400 font-bold uppercase tracking-wider border-b border-slate-100">
                        <th className="text-left pb-3">Clinic</th>
                        <th className="text-right pb-3">Bookings</th>
                        <th className="text-right pb-3">Patient Revenue</th>
                        <th className="text-center pb-3">Commission Rate</th>
                        <th className="text-right pb-3">Platform Commission</th>
                        <th className="text-right pb-3">Settled</th>
                        <th className="text-right pb-3">Pending to Collect</th>
                        <th className="text-center pb-3">Action</th>
                      </tr>
                    </thead>
                    <tbody>
                      {commClinics.map((row, i) => {
                        const pending = Math.max(0, Number(row.commission || 0) - Number(row.settled_amount || 0));
                        return (
                          <tr key={i} className="border-b border-slate-50 hover:bg-slate-50 transition-colors">
                            <td className="py-3.5">
                              <div className="flex items-center gap-2">
                                <div className="w-7 h-7 rounded-lg bg-purple-50 text-purple-700 flex items-center justify-center text-xs font-bold border border-purple-100">
                                  <Building2 size={13} />
                                </div>
                                <div>
                                  <p className="font-semibold text-slate-800">{row.clinic_name || row.vendor_name || `Clinic #${row.clinic_id}`}</p>
                                  <p className="text-[11px] text-slate-400">ID #{row.clinic_id}</p>
                                </div>
                              </div>
                            </td>
                            <td className="py-3.5 text-right text-slate-600 font-medium">{Number(row.bookings).toLocaleString()}</td>
                            <td className="py-3.5 text-right font-bold text-slate-700">₹ {fmt(row.revenue)}</td>
                            <td className="py-3.5 text-center">
                              <span className="bg-purple-50 text-purple-700 px-2 py-0.5 rounded-full text-xs font-bold border border-purple-200">
                                {row.commission_type === 'percentage' ? `${row.commission_rate}%` : `₹ ${fmt(row.commission_rate)}/bk`}
                              </span>
                            </td>
                            <td className="py-3.5 text-right font-bold text-teal-700">₹ {fmt(row.commission)}</td>
                            <td className="py-3.5 text-right font-bold text-emerald-700">₹ {fmt(row.settled_amount)}</td>
                            <td className="py-3.5 text-right font-bold text-orange-700">₹ {fmt(pending)}</td>
                            <td className="py-3.5 text-center">
                              <button
                                onClick={() => openSettlementModal(row, 'commission', pending)}
                                className="px-3 py-1 bg-teal-50 hover:bg-teal-100 text-teal-700 border border-teal-200 rounded-lg text-xs font-bold transition-all"
                              >
                                Settle
                              </button>
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                    <tfoot>
                      <tr className="border-t-2 border-slate-200 bg-slate-50">
                        <td className="py-3 font-bold text-slate-800 px-2">Total</td>
                        <td className="py-3 text-right font-bold text-slate-700">{Number(s.total_bookings || 0).toLocaleString()}</td>
                        <td className="py-3 text-right font-bold text-slate-800">₹ {fmt(s.total_revenue)}</td>
                        <td className="py-3 text-center font-bold text-slate-500">—</td>
                        <td className="py-3 text-right font-bold text-teal-800">₹ {fmt(s.total_commission)}</td>
                        <td className="py-3 text-right font-bold text-emerald-800">₹ {fmt(s.total_commission_settled)}</td>
                        <td className="py-3 text-right font-bold text-orange-800">₹ {fmt(s.pending_commission)}</td>
                        <td className="py-3 text-center font-bold text-slate-400">—</td>
                      </tr>
                    </tfoot>
                  </table>
                </div>
              )}
            </div>
          )}

          {/* TAB 2: Subscription Model Clinics */}
          {activeTab === 'subscription' && (
            <div className="bg-white rounded-2xl border border-slate-100 shadow-sm p-5">
              <div className="flex items-center justify-between mb-4">
                <h2 className="text-sm font-bold text-slate-700 flex items-center gap-2">
                  <Calendar size={16} className="text-teal-600" />
                  Clinic Subscription Plans &amp; Validity
                </h2>
                <span className="text-xs text-slate-400 font-medium">Flat subscription fee tracking</span>
              </div>

              {subClinics.length === 0 ? (
                <div className="text-center py-10 text-slate-400 text-sm">No clinics currently on the subscription model.</div>
              ) : (
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="text-xs text-slate-400 font-bold uppercase tracking-wider border-b border-slate-100">
                        <th className="text-left pb-3">Clinic</th>
                        <th className="text-right pb-3">Subscription Fee</th>
                        <th className="text-center pb-3">Cycle</th>
                        <th className="text-center pb-3">Start Date</th>
                        <th className="text-center pb-3">End Date</th>
                        <th className="text-center pb-3">Validity Status</th>
                        <th className="text-center pb-3">Payment Status</th>
                      </tr>
                    </thead>
                    <tbody>
                      {subClinics.map((row, i) => {
                        const statusColors = {
                          Active: 'bg-emerald-50 text-emerald-700 border-emerald-200',
                          'Expiring Soon': 'bg-amber-50 text-amber-700 border-amber-200',
                          Expired: 'bg-red-50 text-red-700 border-red-200',
                          'No Date Set': 'bg-slate-100 text-slate-600 border-slate-200',
                        };
                        const badgeStyle = statusColors[row.subscription_status] || statusColors['Active'];
                        return (
                          <tr key={i} className="border-b border-slate-50 hover:bg-slate-50 transition-colors">
                            <td className="py-3.5">
                              <div className="flex items-center gap-2">
                                <div className="w-7 h-7 rounded-lg bg-blue-50 text-blue-700 flex items-center justify-center text-xs font-bold border border-blue-100">
                                  <Building2 size={13} />
                                </div>
                                <div>
                                  <p className="font-semibold text-slate-800">{row.clinic_name || row.vendor_name || `Clinic #${row.clinic_id}`}</p>
                                  <p className="text-[11px] text-slate-400">ID #{row.clinic_id}</p>
                                </div>
                              </div>
                            </td>
                            <td className="py-3.5 text-right font-bold text-teal-700">
                              ₹ {fmt(row.subscription_amount)}
                            </td>
                            <td className="py-3.5 text-center capitalize font-medium text-slate-600">
                              {row.subscription_type || 'Monthly'}
                            </td>
                            <td className="py-3.5 text-center text-slate-600 text-xs font-medium">
                              {row.billing_start_date ? row.billing_start_date.slice(0, 10) : '—'}
                            </td>
                            <td className="py-3.5 text-center text-slate-600 text-xs font-medium">
                              {row.billing_end_date ? row.billing_end_date.slice(0, 10) : '—'}
                            </td>
                            <td className="py-3.5 text-center">
                              <span className={`px-2.5 py-0.5 rounded-full text-xs font-bold border ${badgeStyle}`}>
                                {row.subscription_status}
                                {row.days_remaining !== null && row.days_remaining >= 0 && row.subscription_status !== 'Expired' && (
                                  <span className="text-[10px] ml-1 opacity-75">({row.days_remaining}d left)</span>
                                )}
                              </span>
                            </td>
                            <td className="py-3.5 text-center">
                              <span className="px-2.5 py-0.5 rounded-full text-xs font-bold bg-blue-50 text-blue-700 border border-blue-200">
                                Upfront Paid
                              </span>
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          )}

          {/* TAB 3: Settlement History */}
          {activeTab === 'appointments' && (
            <div className="bg-white rounded-2xl border border-slate-100 shadow-sm p-5">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-sm font-bold text-slate-700">Appointment-wise Revenue Detail ({appointments.length} records)</h3>
                <div className="flex gap-2">
                  <button onClick={printAppointments} className="flex items-center gap-1 px-3 py-1.5 text-xs bg-teal-600 text-white rounded-lg hover:bg-teal-700 transition-all">
                    <Printer size={13} /> Print Report
                  </button>
                  <button onClick={downloadAppointmentsCSV} className="flex items-center gap-1 px-3 py-1.5 text-xs bg-slate-700 text-white rounded-lg hover:bg-slate-800 transition-all">
                    <Download size={13} /> Download CSV
                  </button>
                </div>
              </div>
              {appointmentsLoading ? (
                <div className="text-center py-8 text-slate-400 text-sm">Loading appointments...</div>
              ) : appointments.length === 0 ? (
                <div className="text-center py-8 text-slate-400 text-sm">No appointments found for the selected period.</div>
              ) : (
                <div className="overflow-x-auto">
                  <table className="w-full text-xs text-slate-700">
                    <thead>
                      <tr className="bg-slate-50 border-b border-slate-200">
                        <th className="py-2 px-3 text-left font-semibold">#</th>
                        <th className="py-2 px-3 text-left font-semibold">Date</th>
                        <th className="py-2 px-3 text-left font-semibold">Time</th>
                        <th className="py-2 px-3 text-left font-semibold">Patient</th>
                        <th className="py-2 px-3 text-left font-semibold">Clinic</th>
                        <th className="py-2 px-3 text-left font-semibold">Doctor</th>
                        <th className="py-2 px-3 text-left font-semibold">Service</th>
                        <th className="py-2 px-3 text-right font-semibold">Amount</th>
                        <th className="py-2 px-3 text-right font-semibold">Commission</th>
                      </tr>
                    </thead>
                    <tbody>
                      {appointments.map((a, i) => (
                        <tr key={a.booking_id} className="border-b border-slate-100 hover:bg-slate-50">
                          <td className="py-2 px-3">{i + 1}</td>
                          <td className="py-2 px-3">{a.book_date || '-'}</td>
                          <td className="py-2 px-3">{a.book_time || '-'}</td>
                          <td className="py-2 px-3">{a.patient_name || '-'}</td>
                          <td className="py-2 px-3">{a.clinic_name || '-'}</td>
                          <td className="py-2 px-3">{a.doctor_name || '-'}</td>
                          <td className="py-2 px-3">{a.service_type || '-'}</td>
                          <td className="py-2 px-3 text-right font-mono">{fmt(a.book_amount)}</td>
                          <td className="py-2 px-3 text-right font-mono">{fmt(a.commission_amount)}</td>
                        </tr>
                      ))}
                    </tbody>
                    <tfoot>
                      <tr className="bg-slate-100 font-bold">
                        <td colSpan="7" className="py-2 px-3">Total ({appointments.length} appointments)</td>
                        <td className="py-2 px-3 text-right font-mono">{fmt(appointments.reduce((s,a)=>s+Number(a.book_amount||0),0))}</td>
                        <td className="py-2 px-3 text-right font-mono">{fmt(appointments.reduce((s,a)=>s+Number(a.commission_amount||0),0))}</td>
                      </tr>
                    </tfoot>
                  </table>
                </div>
              )}
            </div>
          )}

          {activeTab === 'settlements' && (
            <div className="bg-white rounded-2xl border border-slate-100 shadow-sm p-5">
              <div className="flex items-center justify-between mb-4">
                <h2 className="text-sm font-bold text-slate-700 flex items-center gap-2">
                  <FileText size={16} className="text-teal-600" />
                  Settlement Transaction History
                </h2>
                <span className="text-xs text-slate-400 font-medium">Audited record of collected payments &amp; settlements</span>
              </div>

              {settlementsLoading ? (
                <div className="loading py-8"><div className="spinner"></div></div>
              ) : settlements.length === 0 ? (
                <div className="text-center py-10 text-slate-400 text-sm">No settlements recorded yet.</div>
              ) : (
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="text-xs text-slate-400 font-bold uppercase tracking-wider border-b border-slate-100">
                        <th className="text-left pb-3">Date</th>
                        <th className="text-left pb-3">Clinic</th>
                        <th className="text-center pb-3">Type</th>
                        <th className="text-center pb-3">Period</th>
                        <th className="text-right pb-3">Amount</th>
                        <th className="text-left pb-3">Payment Reference</th>
                        <th className="text-left pb-3">Notes</th>
                      </tr>
                    </thead>
                    <tbody>
                      {settlements.map((st, i) => (
                        <tr key={i} className="border-b border-slate-50 hover:bg-slate-50 transition-colors">
                          <td className="py-3 font-medium text-slate-600 text-xs">
                            {st.settlement_date ? st.settlement_date.slice(0, 10) : '—'}
                          </td>
                          <td className="py-3 font-semibold text-slate-800">
                            {st.clinic_name || st.vendor_name || `Clinic #${st.clinic_id}`}
                          </td>
                          <td className="py-3 text-center">
                            <span className={`px-2.5 py-0.5 rounded-full text-xs font-bold border ${
                              st.settlement_type === 'subscription'
                                ? 'bg-blue-50 text-blue-700 border-blue-200'
                                : 'bg-purple-50 text-purple-700 border-purple-200'
                            }`}>
                              {st.settlement_type === 'subscription' ? 'Subscription' : 'Commission'}
                            </span>
                          </td>
                          <td className="py-3 text-center text-xs text-slate-500">
                            {st.period_from ? `${st.period_from.slice(0, 10)} to ${st.period_to ? st.period_to.slice(0, 10) : '—'}` : '—'}
                          </td>
                          <td className="py-3 text-right font-bold text-emerald-700">
                            ₹ {fmt(st.amount)}
                          </td>
                          <td className="py-3 text-xs font-mono text-slate-600">
                            {st.payment_reference || '—'}
                          </td>
                          <td className="py-3 text-xs text-slate-500 max-w-[200px] truncate">
                            {st.notes || '—'}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          )}
        </>
      )}

      {/* Record Settlement Modal */}
      {isModalOpen && (
        <div className="modal-overlay" style={{
          position: 'fixed', top: 0, left: 0, right: 0, bottom: 0,
          background: 'rgba(15, 23, 42, 0.6)', backdropFilter: 'blur(4px)',
          display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000,
        }}>
          <div style={{
            background: '#ffffff', borderRadius: '16px', padding: '24px',
            width: '100%', maxWidth: '480px', boxShadow: '0 20px 25px -5px rgba(0, 0, 0, 0.1)',
          }}>
            <div className="flex items-center justify-between mb-4 pb-3 border-b border-slate-100">
              <h3 className="text-base font-bold text-slate-800 flex items-center gap-2">
                <CreditCard size={18} className="text-teal-600" />
                Record Commission Settlement
              </h3>
              <button onClick={() => setIsModalOpen(false)} className="text-slate-400 hover:text-slate-600">
                <X size={18} />
              </button>
            </div>

            <form onSubmit={handleRecordSettlement}>
              <div className="space-y-3 mb-5">
                <div>
                  <label className="block text-xs font-bold text-slate-700 uppercase tracking-wider mb-1">Commission Clinic *</label>
                  <select
                    value={modalForm.clinic_id}
                    onChange={e => setModalForm({ ...modalForm, clinic_id: e.target.value })}
                    className="w-full px-3 py-2 rounded-xl border border-slate-200 text-sm font-medium focus:outline-none focus:border-teal-600"
                    required
                  >
                    <option value="">Select Clinic</option>
                    {clinics.map(c => (
                      <option key={c.id} value={c.id}>
                        {c.vendor_contact || c.vendor_name || `Clinic #${c.id}`} (ID: {c.id}) {c.billing_model === 'subscription' ? '[Subscription]' : '[Commission]'}
                      </option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className="block text-xs font-bold text-slate-700 uppercase tracking-wider mb-1">Settlement Amount (₹) *</label>
                  <input
                    type="number"
                    min="0.01"
                    step="0.01"
                    value={modalForm.amount}
                    onChange={e => setModalForm({ ...modalForm, amount: e.target.value })}
                    placeholder="e.g. 1500"
                    className="w-full px-3 py-2 rounded-xl border border-slate-200 text-sm font-medium focus:outline-none focus:border-teal-600"
                    required
                  />
                </div>

                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <label className="block text-xs font-bold text-slate-700 uppercase tracking-wider mb-1">Period From</label>
                    <input
                      type="date"
                      value={modalForm.period_from}
                      onChange={e => setModalForm({ ...modalForm, period_from: e.target.value })}
                      className="w-full px-3 py-2 rounded-xl border border-slate-200 text-sm font-medium focus:outline-none focus:border-teal-600"
                    />
                  </div>
                  <div>
                    <label className="block text-xs font-bold text-slate-700 uppercase tracking-wider mb-1">Period To</label>
                    <input
                      type="date"
                      value={modalForm.period_to}
                      onChange={e => setModalForm({ ...modalForm, period_to: e.target.value })}
                      className="w-full px-3 py-2 rounded-xl border border-slate-200 text-sm font-medium focus:outline-none focus:border-teal-600"
                    />
                  </div>
                </div>

                <div>
                  <label className="block text-xs font-bold text-slate-700 uppercase tracking-wider mb-1">Payment Reference (UTR / Cheque / Cash)</label>
                  <input
                    type="text"
                    value={modalForm.payment_reference}
                    onChange={e => setModalForm({ ...modalForm, payment_reference: e.target.value })}
                    placeholder="e.g. UTR123456789 or Cash"
                    className="w-full px-3 py-2 rounded-xl border border-slate-200 text-sm font-medium focus:outline-none focus:border-teal-600"
                  />
                </div>

                <div>
                  <label className="block text-xs font-bold text-slate-700 uppercase tracking-wider mb-1">Notes / Remarks</label>
                  <textarea
                    rows={2}
                    value={modalForm.notes}
                    onChange={e => setModalForm({ ...modalForm, notes: e.target.value })}
                    placeholder="Optional remarks regarding this settlement"
                    className="w-full px-3 py-2 rounded-xl border border-slate-200 text-sm font-medium focus:outline-none focus:border-teal-600"
                  />
                </div>
              </div>

              <div className="flex justify-end gap-2">
                <button
                  type="button"
                  onClick={() => setIsModalOpen(false)}
                  className="px-4 py-2 border border-slate-200 rounded-xl text-xs font-bold text-slate-600 hover:bg-slate-50 transition-all"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={savingSettlement}
                  className="px-4 py-2 bg-teal-600 hover:bg-teal-700 text-white rounded-xl text-xs font-bold transition-all disabled:opacity-50"
                >
                  {savingSettlement ? 'Saving...' : 'Confirm Settlement'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default RevenueReport;
